import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, mergeProps as _mergeProps, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, createSlots as _createSlots, createTextVNode as _createTextVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "z-10 table-head" }
const _hoisted_2 = ["onClick"]
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "column-header" }
const _hoisted_5 = {
  key: 1,
  class: "flex justify-center items-center max-w-full h-40 bg-transparent dark:bg-transparent row-bg text-secondary"
}
const _hoisted_6 = {
  key: 2,
  class: "w-full whitespace-normal table-fixed"
}

import useBreakpoints from '@/composables/useBreakpoints';
import { sortBy, sumBy } from 'lodash';
import { computed, onMounted, ref, toRef, watch } from 'vue';
import BalIcon from '../BalIcon/BalIcon.vue';
import BalTableRow from './BalTableRow.vue';
import PinHeader from './PinHeader.vue';
import TotalsRow from './TotalsRow.vue';
import { ColumnDefinition, Data, Sticky } from './types';

type InitialState = {
  sortDirection: 'asc' | 'desc' | null;
  sortColumn: string | null;
};

type DataPinState = {
  // the key inside each data object that you want to
  // pin by
  pinOn: string;
  pinnedData: string[];
};

type Props = {
  columns: ColumnDefinition[];
  data: Data[];
  isLoading?: boolean;
  isLoadingMore?: boolean;
  skeletonClass?: string;
  // eslint-disable-next-line vue/require-default-prop -- TODO: Define default prop
  onRowClick?: (data: Data) => void;
  // eslint-disable-next-line vue/require-default-prop -- TODO: Define default prop
  sticky?: Sticky;
  square?: boolean;
  isPaginated?: boolean;
  noResultsLabel?: string;
  link?: {
    to: string;
    getParams: (data: any) => Record<string, string>;
  } | null;
  initialState?: InitialState;
  // eslint-disable-next-line vue/require-default-prop -- TODO: Define default prop
  pin?: DataPinState | null;
  getTableRowClass?: (rowData: Data, rowIndex: number) => string;
  noPadding?: boolean;
  plainTable?: boolean;
};


export default /*#__PURE__*/_defineComponent({
  props: {
    columns: null,
    data: null,
    isLoading: { type: Boolean, default: false },
    isLoadingMore: { type: Boolean, default: false },
    skeletonClass: { default: '' },
    onRowClick: null,
    sticky: null,
    square: { type: Boolean, default: false },
    isPaginated: { type: Boolean, default: false },
    noResultsLabel: { default: '' },
    link: { default: null },
    initialState: { default: () => ({
    sortColumn: null,
    sortDirection: null,
  }) },
    pin: null,
    getTableRowClass: { type: Function, default: () => '' },
    noPadding: { type: Boolean },
    plainTable: { type: Boolean }
  },
  emits: ['loadMore'],
  setup(__props: any) {

const props = __props as { columns: ColumnDefinition[], data: Data[], isLoading: boolean, isLoadingMore: boolean, skeletonClass: string, onRowClick?: (data: Data) => void, sticky?: Sticky, square: boolean, isPaginated: boolean, noResultsLabel: string, link: {
    to: string;
    getParams: (data: any) => Record<string, string>;
  } | null, initialState: InitialState, pin?: DataPinState | null, getTableRowClass: (rowData: Data, rowIndex: number) => string, noPadding?: boolean, plainTable?: boolean }





const stickyHeaderRef = ref();
const isColumnStuck = ref(false);
const tableData = ref(props.data);
const currentSortDirection = ref<InitialState['sortDirection']>(
  props.initialState?.sortDirection || null
);
const currentSortColumn = ref<InitialState['sortColumn']>(
  props.initialState?.sortColumn || null
);
const headerRef = ref<HTMLElement>();
const bodyRef = ref<HTMLElement>();

const getTableRowClass = toRef(props, 'getTableRowClass');

const { isDesktop } = useBreakpoints();

// for loading and no results
const placeholderBlockWidth = computed(() => sumBy(props.columns, 'width'));

// Need a method for horizontal stickiness as we need to
// check whether the table item belongs in the first column
const getHorizontalStickyClass = (index: number) => {
  if (index !== 0) return '';
  if (props.sticky === 'horizontal' || props.sticky === 'both') {
    return 'horizontalSticky';
  }
  return '';
};

const handleSort = (columnId: string | null, updateDirection = true) => {
  const column = props.columns.find(column => column.id === columnId);
  if (!column?.sortKey) return;
  if (columnId !== currentSortColumn.value) currentSortDirection.value = null;

  currentSortColumn.value = columnId;

  if (updateDirection) {
    if (currentSortDirection.value === null) {
      currentSortDirection.value = 'desc';
    } else if (currentSortDirection.value === 'desc') {
      currentSortDirection.value = 'asc';
    } else {
      currentSortDirection.value = null;
    }
  }

  const sortedData = sortBy(
    (props.data as any).value || props.data,
    column.sortKey
  );
  if (currentSortDirection.value === 'asc') {
    tableData.value = sortedData;
    return;
  } else if (currentSortDirection.value === 'desc') {
    tableData.value = sortedData.reverse();
    return;
  }
  tableData.value = props.data;
};

function getAlignProperty(align: 'left' | 'right' | 'center' | undefined) {
  switch (align) {
    case 'left':
      return 'justify-start';
    case 'right':
      return 'justify-end';
    case 'center':
      return 'justify-center';
    default:
      return 'justify-start';
  }
}

onMounted(() => {
  if (bodyRef.value) {
    bodyRef.value.onscroll = () => {
      if (bodyRef.value && stickyHeaderRef.value) {
        const offsetRatio =
          bodyRef.value.offsetWidth / stickyHeaderRef.value.offsetWidth / 10;
        isColumnStuck.value = !!(
          stickyHeaderRef.value.offsetLeft >
          stickyHeaderRef.value.offsetWidth * offsetRatio
        );
      }
    };
    bodyRef.value.addEventListener('scroll', () => {
      if (bodyRef.value && headerRef.value) {
        headerRef.value.scrollLeft = bodyRef.value.scrollLeft;
      }
    });
  }

  handleSort(currentSortColumn.value, false);
});

/**
 * COMPUTED
 */
const unpinnedData = computed(() => {
  if (!props.pin) return tableData.value;
  return (tableData.value || []).filter(
    data => !props.pin?.pinnedData.includes(data[props.pin.pinOn])
  );
});

const pinnedData = computed(() => {
  if (!props.pin) return [];
  return (tableData.value || []).filter(data =>
    props.pin?.pinnedData.includes(data[props.pin.pinOn])
  );
});

const filteredColumns = computed(() =>
  props.columns.filter(column => !column.hidden)
);

const shouldRenderTotals = computed(() =>
  props.columns.some(column => column.totalsCell !== undefined)
);

watch(
  () => props.data,
  newData => {
    if (currentSortColumn.value && currentSortDirection.value !== null) {
      handleSort(currentSortColumn.value, false);
      return;
    }
    tableData.value = newData;
  }
);

return (_ctx: any,_cache: any) => {
  const _component_BalLoadingBlock = _resolveComponent("BalLoadingBlock")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: _normalizeClass([
      `table-top max-w-full whitespace-nowrap ${
        _unref(isDesktop) ? 'overflow-hidden' : 'overflow-x-auto'
      }`,
      { 'rounded-lg': !__props.square },
    ])
    }, [
      _createElementVNode("div", {
        ref: (_value, _refs) => {
          _refs['headerRef'] = _value
          headerRef.value = _value
        },
        class: _normalizeClass(_unref(isDesktop) ? 'overflow-hidden' : '')
      }, [
        _createElementVNode("table", {
          class: _normalizeClass(["w-full whitespace-normal table-fixed", [props.plainTable ? '' : 'table-styles']])
        }, [
          _createElementVNode("colgroup", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(filteredColumns), (column) => {
              return (_openBlock(), _createElementBlock("col", {
                key: column.id,
                style: _normalizeStyle({ width: `${column?.width}px` })
              }, null, 4))
            }), 128))
          ]),
          _createElementVNode("thead", _hoisted_1, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(filteredColumns), (column, columnIndex) => {
              return (_openBlock(), _createElementBlock("th", {
                key: `header-${column.id}`,
                ref: columnIndex == 0 ? 'stickyHeaderRef' : undefined,
                class: _normalizeClass([
              'table-border',
              !props.noPadding ? 'table-border-padding' : '',
              column.className,
              getHorizontalStickyClass(columnIndex),
              isColumnStuck.value ? 'isSticky' : '',
              column.sortKey ? 'cursor-pointer' : '',
              currentSortColumn.value === column.id && currentSortDirection.value
                ? 'text-black hover:text-red-900 focus:text-red-900 dark:text-white dark:hover:text-red-900 dark:focus:text-red-900 transition-colors'
                : 'text-gray-800 hover:text-red-900 focus:text-red-900 dark:text-white dark:hover:text-red-900 dark:focus:text-red-900 transition-colors',
            ]),
                onClick: ($event: any) => (handleSort(column.id))
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass(['flex', getAlignProperty(column.align)])
                }, [
                  (column.Header)
                    ? _renderSlot(_ctx.$slots, column.Header, _normalizeProps(_mergeProps({ key: 0 }, column)))
                    : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                        _createElementVNode("h5", _hoisted_4, _toDisplayString(column.name), 1)
                      ])),
                  (
                  currentSortColumn.value === column.id &&
                  currentSortDirection.value === 'asc'
                )
                    ? (_openBlock(), _createBlock(BalIcon, {
                        key: 2,
                        name: "arrow-up",
                        size: "sm",
                        class: "flex items-center ml-1"
                      }))
                    : _createCommentVNode("", true),
                  (
                  currentSortColumn.value === column.id &&
                  currentSortDirection.value === 'desc'
                )
                    ? (_openBlock(), _createBlock(BalIcon, {
                        key: 3,
                        name: "arrow-down",
                        size: "sm",
                        class: "flex items-center ml-1"
                      }))
                    : _createCommentVNode("", true)
                ], 2)
              ], 10, _hoisted_2))
            }), 128))
          ])
        ], 2)
      ], 2),
      _createElementVNode("div", {
        ref: (_value, _refs) => {
          _refs['bodyRef'] = _value
          bodyRef.value = _value
        },
        class: ""
      }, [
        (__props.isLoading)
          ? (_openBlock(), _createBlock(_component_BalLoadingBlock, {
              key: 0,
              class: _normalizeClass([__props.skeletonClass, 'min-w-full']),
              square: "",
              style: _normalizeStyle({ width: `${_unref(placeholderBlockWidth)}px` })
            }, null, 8, ["class", "style"]))
          : (!__props.isLoading && !tableData.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(__props.noResultsLabel || _ctx.$t('noResults')), 1))
            : (_openBlock(), _createElementBlock("table", _hoisted_6, [
                _createElementVNode("colgroup", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(filteredColumns), (column) => {
                    return (_openBlock(), _createElementBlock("col", {
                      key: column.id,
                      style: _normalizeStyle({ width: `${column?.width}px` })
                    }, null, 4))
                  }), 128))
                ]),
                _createElementVNode("tr", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(filteredColumns), (column, columnIndex) => {
                    return (_openBlock(), _createElementBlock("td", {
                      key: column.id,
                      class: _normalizeClass([
              column.align === 'right' ? 'text-left' : 'text-right',
              getHorizontalStickyClass(columnIndex),
              isColumnStuck.value ? 'isSticky' : '',
              'bg-transparent dark:bg-transparent p-0 m-0 h-0',
            ])
                    }, null, 2))
                  }), 128))
                ]),
                (_unref(pinnedData).length)
                  ? (_openBlock(), _createBlock(PinHeader, { key: 0 }))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(pinnedData), (dataItem, index) => {
                  return (_openBlock(), _createBlock(BalTableRow, {
                    key: `tableRow-${index}`,
                    class: _normalizeClass(_unref(getTableRowClass)(dataItem, index)),
                    data: dataItem,
                    columns: _unref(filteredColumns),
                    onRowClick: __props.onRowClick,
                    link: __props.link,
                    sticky: __props.sticky,
                    isColumnStuck: isColumnStuck.value,
                    pinned: "",
                    noPadding: __props.noPadding
                  }, _createSlots({ _: 2 }, [
                    _renderList(_ctx.$slots, (_, name) => {
                      return {
                        name: name,
                        fn: _withCtx((slotData) => [
                          _renderSlot(_ctx.$slots, name, _normalizeProps(_guardReactiveProps(slotData)))
                        ])
                      }
                    })
                  ]), 1032, ["class", "data", "columns", "onRowClick", "link", "sticky", "isColumnStuck", "noPadding"]))
                }), 128)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(unpinnedData), (dataItem, index) => {
                  return (_openBlock(), _createBlock(BalTableRow, {
                    key: `tableRow-${index}`,
                    noPadding: __props.noPadding,
                    class: _normalizeClass(
            props.getTableRowClass
              ? props.getTableRowClass(dataItem, index)
              : undefined
          ),
                    data: dataItem,
                    columns: _unref(filteredColumns),
                    onRowClick: __props.onRowClick,
                    link: __props.link,
                    sticky: __props.sticky,
                    isColumnStuck: isColumnStuck.value
                  }, _createSlots({ _: 2 }, [
                    _renderList(_ctx.$slots, (_, name) => {
                      return {
                        name: name,
                        fn: _withCtx((slotData) => [
                          _renderSlot(_ctx.$slots, name, _normalizeProps(_guardReactiveProps(slotData)))
                        ])
                      }
                    })
                  ]), 1032, ["noPadding", "class", "data", "columns", "onRowClick", "link", "sticky", "isColumnStuck"]))
                }), 128)),
                (!__props.isLoading && tableData.value.length && _unref(shouldRenderTotals))
                  ? (_openBlock(), _createBlock(TotalsRow, {
                      key: 1,
                      columns: _unref(filteredColumns),
                      onRowClick: __props.onRowClick,
                      sticky: __props.sticky,
                      isColumnStuck: isColumnStuck.value
                    }, _createSlots({ _: 2 }, [
                      _renderList(_ctx.$slots, (_, name) => {
                        return {
                          name: name,
                          fn: _withCtx((slotData) => [
                            _renderSlot(_ctx.$slots, name, _normalizeProps(_guardReactiveProps(slotData)))
                          ])
                        }
                      })
                    ]), 1032, ["columns", "onRowClick", "sticky", "isColumnStuck"]))
                  : _createCommentVNode("", true)
              ]))
      ], 512)
    ], 2),
    (__props.isPaginated && !__props.isLoading)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "bal-table-pagination-btn text-secondary",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (!__props.isLoadingMore && _ctx.$emit('loadMore')))
        }, [
          (__props.isLoadingMore)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createTextVNode(_toDisplayString(_ctx.$t('loading')), 1)
              ], 64))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createTextVNode(_toDisplayString(_ctx.$t('loadMore')) + " ", 1),
                _createVNode(BalIcon, {
                  name: "chevron-down",
                  size: "sm",
                  class: "ml-2"
                })
              ], 64))
        ]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})